import React from 'react';
import { reduce, last, contains, init, append, split, over, lensProp, defaultTo, equals, keys, path, map, assocPath, without, not, isNil, dissocPath, lensPath, evolve, always, F, pathOr, pluck, has, merge, flatten, omit, reject, startsWith, endsWith, join, objOf, allPass, propEq, complement, cond, T, prop, head, zipWith, assoc, times, identity, length, takeLast } from 'ramda';
import { createSelectorCreator, defaultMemoize, createSelector, createStructuredSelector } from 'reselect';
import assert from 'assert';
import validateSchema from '@kinday/njv';
import PropTypes from 'prop-types';
import { Box as Box$1 } from '@rushplay/common';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function fromSchemaPath(path) {
  return reduce(function (scope, segment) {
    return "".concat(scope, "/").concat(segment);
  }, '#', path);
}
function fromPath(path) {
  return reduce(function (scope, segment) {
    if (typeof segment === 'number') {
      return "".concat(scope, "/").concat(segment);
    }

    return "".concat(scope, "/properties/").concat(segment);
  }, '#', path);
}
function toSchemaPath(scope) {
  return reduce(function (path, segment) {
    if (segment === '#') {
      return path;
    } // If segment is number, check if it’s property name or array index


    if (segment == parseInt(segment) && last(path) !== 'properties') {
      return append(parseInt(segment), path);
    }

    return append(segment, path);
  }, [], split('/', scope));
}
var JSON_SCHEMA_GROUPS = ['allOf', 'anyOf', 'definitions', 'oneOf'];
var JSON_SCHEMA_PROPERTIES = ['additionalItems', 'additionalProperties', 'contains', 'dependencies', 'not', 'pattern', 'patternProperties', 'properties', 'then'];
function toDataPath(scope) {
  return reduce(function (path, segment) {
    var prevSegment = last(path);

    if (contains(prevSegment, JSON_SCHEMA_GROUPS)) {
      return init(path);
    }

    if (contains(segment, JSON_SCHEMA_PROPERTIES)) {
      return path;
    }

    return append(segment, path);
  }, [], toSchemaPath(scope));
}
function toDataScope(scope) {
  return fromPath(toDataPath(scope));
}
function toFieldName(scope) {
  return reduce(function (name, segment) {
    return name == null ? segment : "".concat(name, "[").concat(segment, "]");
  }, null, toDataPath(scope));
}
function toTranslationSegment(scope) {
  return reduce(function (name, segment) {
    return name == null ? segment : "".concat(name, "-").concat(segment);
  }, null, toDataPath(scope));
}

/**
 * Convert absolute schema path to field’s schema scope
 * @param {Array.<string>} absoluteSchemaPath
 *   Absolute schema path from validation error
 * @param {string} property
 *   Instance property name if validator is on object level (e.g. `required`)
 */

function absoluteSchemaPathToScope(absoluteValidatorPath, property) {
  var absoluteSchemaPath = init(absoluteValidatorPath);
  var absolutePropertyPath = typeof property !== 'string' ? absoluteSchemaPath : append(property, absoluteSchemaPath);
  return fromSchemaPath(absolutePropertyPath);
}
/**
 * @typedef {Object} FieldError
 * @property {string} field Field name
 * @property {string} translationKey Translation key
 * @property {object} variables Translation variables
 */

/**
 * @typedef {Object} FormValidation
 * @property {("valid"|"invalid")} status
 * @property {Object.<FieldError>} errors Field errors indexed by field name
 */


function toFormErrors(validationErrors) {
  var formErrors = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return reduce(function (errors, error) {
    if (error.context == null) {
      var field = error.validator === 'required' ? last(error.absoluteInstancePath) : null;
      var scope = absoluteSchemaPathToScope(error.absoluteSchemaPath, field);
      return over(lensProp(toDataScope(scope)), function (fieldErrors) {
        return append({
          validator: error.validator,
          validatorValue: error.validatorValue
        }, defaultTo([], fieldErrors));
      }, errors);
    }

    return toFormErrors(error.context, errors);
  }, formErrors, validationErrors);
}
/**
 * Validates form
 * @param {string} form Form name
 * @param {Object} schema Data schema
 * @param {Object} fields Form fields
 * @returns {FormValidation}
 */


function validateForm(schema, instance) {
  var result = validateSchema(schema, instance);

  if (result.valid) {
    return {};
  }

  return toFormErrors(result.errors);
}

var EMPTY_ARRAY = Object.freeze([]);
var EMPTY_OBJECT = Object.freeze({});
var FieldStatus = {
  ABSENT: 'ABSENT',
  PRISTINE: 'PRISTINE',
  UNCHANGED: 'UNCHANGED',
  CHANGED: 'CHANGED'
};
var FIELD_CLEARED = 'forms/FIELD_CLEARED';
var FIELD_COMMITTED = 'forms/FIELD_COMMITTED';
var FIELD_DESTROYED = 'forms/FIELD_DESTROYED';
var FIELD_REGISTERED = 'forms/FIELD_REGISTERED';
var FIELD_VISITED = 'forms/FIELD_VISITED';
var FIELD_VALUE_CHANGED = 'forms/FIELD_VALUE_CHANGED';
var FIELDS_CLEARED = 'forms/FIELDS_CLEARED';
var FIELDS_COMMITTED = 'forms/FIELDS_COMMITTED';
var FIELDS_DESTROYED = 'forms/FIELDS_DESTROYED';
var FORM_SCHEMA_CHANGED = 'forms/FORM_SCHEMA_CHANGED';
var FORM_SUBMITTED = 'forms/FORM_SUBMITTED';
var createDeepEqualSelector = createSelectorCreator(defaultMemoize, equals);
function getInitialState() {
  var initialValues = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  return {
    fields: EMPTY_OBJECT,
    schema: initialValues.schema || EMPTY_OBJECT
  };
}
function reducer(state, action) {
  switch (action.type) {
    case FIELD_CLEARED:
      {
        return reduce(reducer, state, [changeFieldValue(action.payload.scope), commitField(action.payload.scope)]);
      }

    case FIELD_COMMITTED:
      {
        var value = path(['fields', action.payload.scope, 'value'], state);
        return over(lensPath(['fields', action.payload.scope]), evolve({
          initialValue: always(value),
          visited: F
        }), state);
      }

    case FIELD_DESTROYED:
      {
        return dissocPath(['fields', action.payload.scope], state);
      }

    case FIELD_REGISTERED:
      {
        if (not(isNil(path(['fields', action.payload.scope], state)))) {
          return state;
        }

        var field = {
          initialValue: action.payload.value,
          scope: action.payload.scope,
          value: action.payload.value,
          visited: false
        };
        return assocPath(['fields', action.payload.scope], field, state);
      }

    case FIELD_VALUE_CHANGED:
      {
        return assocPath(['fields', action.payload.scope, 'value'], action.payload.value, state);
      }

    case FIELD_VISITED:
      {
        return assocPath(['fields', action.payload.scope, 'visited'], true, state);
      }

    case FIELDS_CLEARED:
    case FIELDS_COMMITTED:
    case FIELDS_DESTROYED:
      {
        var _FIELDS_CLEARED$FIELD;

        var scopes = Array.isArray(action.payload.include) ? action.payload.include : without(action.payload.exclude, keys(path(['fields'], state)));
        var fn = (_FIELDS_CLEARED$FIELD = {}, _defineProperty(_FIELDS_CLEARED$FIELD, FIELDS_CLEARED, clearField), _defineProperty(_FIELDS_CLEARED$FIELD, FIELDS_COMMITTED, commitField), _defineProperty(_FIELDS_CLEARED$FIELD, FIELDS_DESTROYED, destroyField), _FIELDS_CLEARED$FIELD)[action.type];
        return reduce(reducer, state, map(fn, scopes));
      }

    case FORM_SCHEMA_CHANGED:
      {
        var prevSchema = state.schema;

        if (prevSchema === action.payload) {
          return state;
        }
        /* istanbul ignore next */


        if (process.env.NODE_ENV !== 'production') {
          // Objects as React props are compared by reference, which may cause
          // redundant context and its consumers renders. If source of your JSON
          // schema does not guarantee referential equality, you must introduce
          // structural equality check and provide new object only if schema did
          // change.
          if (equals(prevSchema, action.payload)) {
            // eslint-disable-next-line no-console
            console.warn('[@rushplay/forms]', 'Attempt to write same JSON schema');
          }
        }

        return assocPath(['schema'], action.payload, state);
      }

    case FORM_SUBMITTED:
      {
        var _scopes = keys(path(['fields'], state));

        return reduce(reducer, state, map(visitField, _scopes));
      }

    default:
      {
        return state;
      }
  }
}
function changeFieldValue(scope, value) {
  assert(scope != null, '"scope" argument is mandatory');
  return {
    type: FIELD_VALUE_CHANGED,
    payload: {
      scope: scope,
      value: value
    }
  };
}
function changeFormSchema(schema) {
  return {
    type: FORM_SCHEMA_CHANGED,
    payload: schema
  };
}
function clearField(scope) {
  assert(scope != null, '"scope" argument is mandatory');
  return {
    type: FIELD_CLEARED,
    payload: {
      scope: scope
    }
  };
}
/**
 * Clear multiple fields
 * @param {Object} [options]
 *   Filtering options; no options will clear all fields
 * @param {Array.<string>} [options.exclude=[]] Fields to be left intact
 * @param {Array.<string>} [options.include=[]] Fields to be commited
 */

function clearFields() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var exclude = defaultTo([], options.exclude);
  var include = Array.isArray(options.include) ? without(exclude, options.include) : options.include;
  return {
    type: FIELDS_CLEARED,
    payload: {
      exclude: exclude,
      include: include
    }
  };
}
/**
 * Clear form fiedls
 */

function clearForm() {
  if (process.env.NODE_ENV) {
    // eslint-disable-next-line no-console
    console.warn('[@rushplay/forms]', 'DEPRECATED', '"clearForm" is deprecated; use "clearFields" instead');
  }

  return clearFields();
}
function commitField(scope) {
  assert(scope != null, '"scope" argument is mandatory');
  return {
    type: FIELD_COMMITTED,
    payload: {
      scope: scope
    }
  };
}
/**
 * Commit multiple fields
 * @param {Object} [options]
 *   Filtering options; no options will commit all fields
 * @param {Array.<string>} [options.exclude=[]] Fields to be left intact
 * @param {Array.<string>} [options.include=[]] Fields to be commited
 */

function commitFields() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var exclude = defaultTo([], options.exclude);
  var include = Array.isArray(options.include) ? without(exclude, options.include) : options.include;
  return {
    type: FIELDS_COMMITTED,
    payload: {
      exclude: exclude,
      include: include
    }
  };
}
function commitForm() {
  if (process.env.NODE_ENV) {
    // eslint-disable-next-line no-console
    console.warn('[@rushplay/forms]', 'DEPRECATED', '"commitForm" is deprecated; use "commitFields" instead');
  }

  return commitFields();
}
function destroyField(scope) {
  assert(scope != null, '"scope" argument is mandatory');
  return {
    type: FIELD_DESTROYED,
    payload: {
      scope: scope
    }
  };
}
/**
 * Destroy multiple fields
 * @param {Object} [options]
 *   Filtering options; no options will destroy all fields
 * @param {Array.<string>} [options.exclude=[]] Fields to be left intact
 * @param {Array.<string>} [options.include=[]] Fields to be commited
 */

function destroyFields() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var exclude = defaultTo([], options.exclude);
  var include = Array.isArray(options.include) ? without(exclude, options.include) : options.include;
  return {
    type: FIELDS_DESTROYED,
    payload: {
      exclude: exclude,
      include: include
    }
  };
}
function registerField(scope, value) {
  assert(scope != null, '"scope" argument is mandatory');
  return {
    type: FIELD_REGISTERED,
    payload: {
      scope: scope,
      value: value
    }
  };
}
function submitForm() {
  return {
    type: FORM_SUBMITTED
  };
}
function visitField(scope) {
  assert(scope != null, '"scope" argument is mandatory');
  return {
    type: FIELD_VISITED,
    payload: {
      scope: scope
    }
  };
}

function __getScope(state, props) {
  return path(['scope'], props);
}

var __getInstanceScope = createSelector([__getScope], toDataScope);

function getFields(state) {
  return pathOr(EMPTY_OBJECT, ['fields'], state);
}
function getSchema(state) {
  return pathOr(EMPTY_OBJECT, ['schema'], state);
}

function __createGetFieldSelector() {
  return createSelector([getFields, __getScope], function (fields, scope) {
    return path([scope], fields);
  });
}

var getFieldValues = createSelector([getFields], function (fields) {
  return pluck('value', fields);
});
var getFormData = createSelector([getFieldValues], function (fields) {
  // Return the value if there’s root scoped field
  if (has('#', fields)) {
    return fields['#'];
  }

  var result = null;

  for (var scope in fields) {
    var value = fields[scope]; // Omit undefined values to correctly validate required fields

    result = typeof value === 'undefined' ? result : assocPath(toDataPath(scope), fields[scope], result);
  }

  return result;
});
var getFormErrors = createDeepEqualSelector([getSchema, getFormData], function (schema, formData) {
  return validateForm(schema, formData);
});
function createGetFieldErrorsSelector() {
  return createSelector([__getInstanceScope, getFormErrors], function (scope, errors) {
    return pathOr(EMPTY_ARRAY, [scope], errors);
  });
}
function createGetFieldStatusSelector() {
  var __getField = __createGetFieldSelector();

  return createSelector([__getField], function (field) {
    if (field == null) {
      return FieldStatus.ABSENT;
    }

    if (field.value === field.initialValue) {
      return field.visited ? FieldStatus.UNCHANGED : FieldStatus.PRISTINE;
    }

    return FieldStatus.CHANGED;
  });
}
function createGetFieldValueSelector() {
  var __getField = __createGetFieldSelector();

  return createSelector([__getField], function (field) {
    return path(['value'], field);
  });
}
function createGetFieldSelector() {
  var getFieldErrors = createGetFieldErrorsSelector();
  var getFieldStatus = createGetFieldStatusSelector();
  var getFieldValue = createGetFieldValueSelector();
  return createStructuredSelector({
    errors: getFieldErrors,
    status: getFieldStatus,
    value: getFieldValue
  });
}

function noop() {}

/**
 * @typedef {Object} FormContextValue
 * @property {(scope: string): void} clearField
 * @property {(action: Object): void} dispatch
 * @property {string} name Form name
 * @property {Object} state Form state
 * @property {(): void} submit
 */

var FormContext = React.createContext({
  clearField: noop,
  clearFields: noop,
  commitField: noop,
  commitFields: noop,
  destroyField: noop,
  destroyFields: noop,
  dispatch: noop,
  name: '',
  state: getInitialState(),
  submit: noop
});

var EffectType = {
  Disable: 'disable',
  Enable: 'enable',
  Hide: 'hide',
  Show: 'show'
};
var NodeType = {
  Box: 'Box',
  Component: 'Component',
  Control: 'Control',
  Fragment: 'Fragment',
  Effect: 'Effect',
  Submit: 'Submit',
  Text: 'Text'
};

/* @jsx createElement */
var EMPTY_ARRAY$1 = Object.freeze([]);
var EMPTY_OBJECT$1 = Object.freeze({});
/* istanbul ignore next: dev-only */

function checkPropTypes(component, props) {
  if (process.env.NODE_ENV !== 'production') {
    var displayName = component.displayName || component.name;
    var propTypes = component.propTypes;

    for (var prop in props) {
      // Ignore compilation artifact
      if (prop === '__source') {
        break;
      }

      if (propTypes[prop] == null) {
        // eslint-disable-next-line no-console
        console.error('Warning:', 'Unknown prop:', "Invalid prop \"".concat(prop, "\" of supplied to \"UiSchema.").concat(displayName, "\""));
      } else {
        PropTypes.checkPropTypes(propTypes, props, prop, displayName);
      }
    }
  }
}

function normalizeNode(node) {
  return reject(isNil, node);
}

function createElement(fn, props) {
  for (var _len = arguments.length, children = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    children[_key - 2] = arguments[_key];
  }

  if (props == null) {
    return createElement.apply(void 0, [fn, {}].concat(children));
  }

  if (children.length === 0) {
    return fn(props);
  }

  return createElement(fn, merge(props, {
    children: flatten(children)
  }));
}
function Box(props) {
  checkPropTypes(Box, props);
  var _props$children = props.children,
      children = _props$children === void 0 ? EMPTY_ARRAY$1 : _props$children,
      _props$effects = props.effects,
      effects = _props$effects === void 0 ? EMPTY_ARRAY$1 : _props$effects;
  var styles = omit(['children', 'effects'], props);
  return normalizeNode({
    __type: NodeType.Box,
    children: children,
    effects: effects,
    styles: styles
  });
}
Box.propTypes = merge(Box$1.propTypes, {
  children: PropTypes.array,
  effects: PropTypes.array
});
function Component(props) {
  checkPropTypes(Component, props);
  var _props$children2 = props.children,
      children = _props$children2 === void 0 ? EMPTY_ARRAY$1 : _props$children2,
      content = props.content,
      _props$effects2 = props.effects,
      effects = _props$effects2 === void 0 ? EMPTY_ARRAY$1 : _props$effects2,
      id = props.id;
  return normalizeNode({
    __type: NodeType.Component,
    children: children,
    content: content,
    effects: effects,
    id: id
  });
}
Component.propTypes = {
  children: PropTypes.array,
  content: PropTypes.node,
  effects: PropTypes.array,
  id: PropTypes.string.isRequired
};
function Control(props) {
  checkPropTypes(Control, props);
  var _props$effects3 = props.effects,
      effects = _props$effects3 === void 0 ? EMPTY_ARRAY$1 : _props$effects3,
      _props$forwardProps = props.forwardProps,
      forwardProps = _props$forwardProps === void 0 ? EMPTY_OBJECT$1 : _props$forwardProps,
      type = props.type,
      scope = props.scope;
  return normalizeNode({
    __type: NodeType.Control,
    effects: effects,
    forwardProps: forwardProps,
    type: type,
    scope: scope
  });
}
Control.propTypes = {
  effects: PropTypes.array,
  forwardProps: PropTypes.object,
  scope: PropTypes.string.isRequired,
  // TODO: Add all <input> types
  type: PropTypes.oneOf(['number', 'password', 'text'])
};

function resolveScopes(baseScope, children) {
  if (baseScope == null) {
    return children;
  }

  return map(function (child) {
    var hasScope = child.scope != null;
    var hasAbsoluteScope = hasScope && child.scope.startsWith('#/');
    var hasResolvableScope = hasScope && !hasAbsoluteScope;
    return normalizeNode(merge(child, {
      children: child.children ? resolveScopes(baseScope, child.children) : null,
      scope: hasResolvableScope ? "".concat(baseScope).concat(child.scope) : child.scope
    }));
  }, children);
}

function Fragment(props) {
  checkPropTypes(Fragment, props);
  var baseScope = props.baseScope,
      _props$children3 = props.children,
      children = _props$children3 === void 0 ? EMPTY_ARRAY$1 : _props$children3,
      _props$effects4 = props.effects,
      effects = _props$effects4 === void 0 ? EMPTY_ARRAY$1 : _props$effects4;

  if (baseScope) {
    assert(startsWith('#/', baseScope), '"baseScope" scope must be absolute path');
    assert(endsWith('/', baseScope), '"baseScope" scope must end with "/"');
  }

  return normalizeNode({
    __type: NodeType.Fragment,
    children: resolveScopes(baseScope, children),
    effects: effects
  });
}
Fragment.propTypes = {
  baseScope: PropTypes.string,
  children: PropTypes.array,
  effects: PropTypes.array
};
function Submit(props) {
  checkPropTypes(Submit, props);
  var content = props.content,
      _props$effects5 = props.effects,
      effects = _props$effects5 === void 0 ? EMPTY_ARRAY$1 : _props$effects5;
  return normalizeNode({
    __type: NodeType.Submit,
    content: content,
    effects: effects
  });
}
Submit.propTypes = {
  content: PropTypes.node,
  effects: PropTypes.array
};
function Text(props) {
  checkPropTypes(Text, props);
  var content = props.content,
      _props$effects6 = props.effects,
      effects = _props$effects6 === void 0 ? EMPTY_ARRAY$1 : _props$effects6;
  return normalizeNode({
    __type: NodeType.Text,
    content: content,
    effects: effects
  });
}
Text.propTypes = {
  content: PropTypes.string,
  effects: PropTypes.array
};

function createEffectCreator(effect) {
  function createEffect(condition) {
    return normalizeNode({
      __type: NodeType.Effect,
      condition: condition,
      effect: effect
    });
  }

  createEffect.displayName = "createEffect<".concat(effect, ">");
  return createEffect;
}

var effects = {
  disable: createEffectCreator('disable'),
  enable: createEffectCreator('enable'),
  hide: createEffectCreator('hide'),
  show: createEffectCreator('show')
};

var uiSchema = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createElement: createElement,
  Box: Box,
  Component: Component,
  Control: Control,
  Fragment: Fragment,
  Submit: Submit,
  Text: Text,
  effects: effects
});

function toKebabCase(str) {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase();
} // TODO: move to @rushplay/i18n


function normalizeTranslationKey(key) {
  return join('.', map(toKebabCase, split('.', key)));
}

/**
 * React hook to retrieve nearest <Forms.Provider> context
 *
 * @returns {FormContextValue}
 */

function useFormContext() {
  var context = React.useContext(FormContext);
  /* istanbul ignore next: dev-only */

  if (process.env.NODE_ENV !== 'production') {
    if (!context) {
      // eslint-disable-next-line no-console
      console.warn('[@rushplay/forms]', 'Missing <FormProvider>; field might work incorrectly');
    }
  }

  return context;
}
function useFormData() {
  var context = useFormContext();
  return getFormData(context.state);
}
function useFormSchema() {
  var context = useFormContext();
  return getSchema(context.state);
}

function normalizeErrors(keyPrefix, errors) {
  return map(function (error) {
    return {
      translationKey: normalizeTranslationKey("".concat(keyPrefix, ".").concat(error.validator)),
      translationVariables: objOf(error.validator, error.validatorValue)
    };
  }, errors);
}

function useField(scope) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var form = useFormContext();
  React.useEffect(function () {
    if (!options.noRegister) {
      form.dispatch(registerField(scope, options.initialValue));

      if (options.destroyOnUnmount) {
        return function () {
          return form.dispatch(destroyField(scope));
        };
      }
    }
  }, [form.dispatch, options.destroyOnUnmount, options.initialValue, options.noRegister, scope]);
  var getField = React.useMemo(createGetFieldSelector, []);
  var name = React.useMemo(function () {
    return toFieldName(scope);
  }, [scope]);
  var keyPrefix = React.useMemo(function () {
    return normalizeTranslationKey("".concat(form.name, ".").concat(toTranslationSegment(scope)));
  }, [form.name, scope]);
  var field = React.useMemo(function () {
    return getField(form.state, {
      scope: scope
    });
  }, [scope, form.state]);
  var value = React.useMemo(function () {
    return typeof options.parse === 'function' ? options.parse(field.value) : field.value;
  }, [field.value, options.parse]);
  var errors = React.useMemo(function () {
    return normalizeErrors(keyPrefix, field.errors);
  }, [field.errors, keyPrefix]);
  var handleBlur = React.useCallback(function () {
    form.dispatch(visitField(scope));
  }, [form.dispatch, scope]);
  var handleChangeValue = React.useCallback(function (value) {
    var normalizedValue = typeof options.normalize === 'function' ? options.normalize(value) : value;
    form.dispatch(changeFieldValue(scope, normalizedValue));
  }, [form.dispatch, options.normalize, scope]);
  var handleChange = React.useCallback(function (event) {
    return handleChangeValue(event.target.value);
  }, []);
  return React.useMemo(function () {
    return {
      errors: errors,
      label: "".concat(keyPrefix, ".label"),
      name: name,
      placeholder: "".concat(keyPrefix, ".placeholder"),
      status: field.status,
      value: value,
      onBlur: handleBlur,
      onChange: handleChange,
      onChangeValue: handleChangeValue
    };
  }, [errors, field.status, keyPrefix, handleBlur, handleChange, name, value]);
}

var isOptionField = has('enum');
var isItemsField = has('items');
var isSingleSelectField = allPass([isOptionField, propEq('type', 'string')]);
var isMultipleSelectField = allPass([isItemsField, propEq('type', 'array')]);
var isTextField = allPass([complement(isOptionField), propEq('type', 'string')]);
var isNumberField = allPass([complement(isOptionField), propEq('type', 'number')]);

function getComponent(components, name) {
  return pathOr(always(null), [name], components);
}

function getInitialValue(schema, initialValues, scope) {
  if (has(scope, initialValues)) {
    return prop(scope, initialValues);
  }

  var schemaSlice = path(toSchemaPath(scope), schema);

  if (isMultipleSelectField(schemaSlice)) {
    return [];
  }

  if (isSingleSelectField(schemaSlice)) {
    return head(defaultTo('', schemaSlice["enum"]));
  }

  return path(['const'], schemaSlice);
}

function normalizeSelectOptions(formName, scope, options) {
  var fieldName = toTranslationSegment(scope);
  return map(function (value) {
    return {
      value: value,
      label: normalizeTranslationKey("".concat(formName, ".").concat(fieldName, ".").concat(value))
    };
  }, options);
}

function getSelectOptions(formName, schema, scope) {
  var schemaSlice = path(toSchemaPath(scope), schema);

  if (isMultipleSelectField(schemaSlice)) {
    return normalizeSelectOptions(formName, scope, schemaSlice.items["enum"]);
  }

  if (isSingleSelectField(schemaSlice)) {
    return normalizeSelectOptions(formName, scope, schemaSlice["enum"]);
  }

  return [];
}

function createEffect(states) {
  var _states = _slicedToArray(states, 2),
      initialState = _states[0],
      activeState = _states[1];

  return function isEffectActive(schema, instance) {
    var result = reduce(function (result, rule) {
      if (contains(rule.effect, states)) {
        if (rule.condition == null) {
          return rule.effect;
        }

        var _validateSchema = validateSchema(rule.condition, instance),
            valid = _validateSchema.valid;

        return valid ? rule.effect : result;
      }

      return result;
    }, initialState, pathOr([], ['effects'], schema));
    return result === activeState;
  };
}

var isToBeDisabled = createEffect(['enable', 'disable']);
var isToBeHidden = createEffect(['show', 'hide']);

function effects$1(schema, data) {
  return {
    disabled: isToBeDisabled(schema, data),
    hidden: isToBeHidden(schema, data)
  };
}
/**
 * Add keys to children recursively (if necessary)
 *
 * Also moves `name` value to `__key` if present for consistency
 *
 * @param {Object} schema
 */


function normalizeUiSchema(schema) {
  if (schema.children == null) {
    return schema;
  }

  var children = map(normalizeUiSchema, zipWith(function (key, child) {
    return assoc('__key', child.name || key, child);
  }, times(identity, length(schema.children)), schema.children));
  return assoc('children', children, schema);
}
/**
 * Gets form fields from given schemas.
 *
 * @param {Object} uiSchema UI schema
 * @param {Object} props JSX attributes
 * @param {Object} props.components Components
 * @param {Function} props.getCustomField Getter for custom fields
 * @param {Object} form Form
 * @returns {Array.<Field>} Field components
 */


function FieldBuilder(props) {
  // TODO: Finish validator
  // validateUiSchema(props.uiSchema)
  var form = useFormContext();
  var formData = getFormData(form.state);
  var formSchema = getSchema(form.state);
  var uiSchema = React.useMemo(function () {
    return normalizeUiSchema(props.uiSchema);
  }, [normalizeUiSchema, props.uiSchema]);

  var _effects = effects$1(uiSchema, formData),
      disabled = _effects.disabled,
      hidden = _effects.hidden;

  if (hidden) {
    return null;
  }

  switch (uiSchema.__type) {
    case NodeType.Box:
      {
        return React.createElement(Box$1, _extends({}, uiSchema.styles, {
          key: uiSchema.__key
        }), map(function (child) {
          return React.createElement(FieldBuilder, _extends({
            key: child.__key
          }, props, {
            uiSchema: child
          }));
        }, uiSchema.children));
      }

    case NodeType.Component:
      {
        var Component = props.components[uiSchema.id];
        /* istanbul ignore next: No good way to test with react-testing-library */

        if (Component == null) {
          throw new Error("Component \"".concat(uiSchema.id, "\" is not defined in props.components"));
        }

        return React.createElement(Component, {
          key: uiSchema.__key
        });
      }

    case NodeType.Control:
      {
        var fieldSchema = path(toSchemaPath(uiSchema.scope), formSchema);

        var _Component = props.getCustomField(uiSchema.scope) || cond([[isSingleSelectField, function () {
          return getComponent(props.components, 'singleSelectField');
        }], [isMultipleSelectField, function () {
          return getComponent(props.components, 'multipleSelectField');
        }], [isTextField, function () {
          return getComponent(props.components, 'textField');
        }], [isNumberField, function () {
          return getComponent(props.components, 'numberField');
        }], [T, always(null)]])(fieldSchema); // TODO: Implement with schema flattening as values can be resolved
        // incorrectly with conditional schemas


        var initialValue = getInitialValue(formSchema, props.initialValues, uiSchema.scope);
        var options = getSelectOptions(form.name, formSchema, uiSchema.scope);
        return React.createElement(_Component, _extends({}, uiSchema.forwardProps, {
          disabled: disabled,
          initialValue: initialValue,
          key: uiSchema.__key,
          format: fieldSchema.format,
          options: options,
          scope: uiSchema.scope,
          type: uiSchema.type
        }));
      }

    case NodeType.Fragment:
      {
        return React.createElement(React.Fragment, {
          key: uiSchema.__key
        }, map(function (child) {
          return React.createElement(FieldBuilder, _extends({
            key: child.__key
          }, props, {
            uiSchema: child
          }));
        }, uiSchema.children));
      }

    case NodeType.Submit:
      {
        var _Component2 = props.components.submit;
        return React.createElement(_Component2, {
          disabled: disabled,
          key: uiSchema.__key
        }, uiSchema.content);
      }

    case NodeType.Text:
      {
        var Text = props.components['text'];
        return React.createElement(Text, {
          key: uiSchema.__key
        }, uiSchema.content);
      }

    /* istanbul ignore next: No good way to test with react-testing-library */

    default:
      {
        throw new Error("\"".concat(uiSchema.type, "\" UI schema type is unknown"));
      }
  }
}
FieldBuilder.defaultProps = {
  components: {},
  getCustomField: function getCustomField() {},
  initialValues: {}
};
FieldBuilder.propTypes = {
  components: PropTypes.object,
  getCustomField: PropTypes.func,
  initialValues: PropTypes.object,
  uiSchema: PropTypes.object.isRequired
};

/**
 * Notify if callbacks given as props seem to be unmemoized
 *
 * Uses naïve check comparing stringified functions.
 *
 * @private
 * @param {Function} nextCallback
 */

function useCallbackEqualityCheck(nextCallback) {
  var ref = React.useRef(nextCallback);
  React.useEffect(function () {
    var prevCallback = ref.current;
    var areFunctions = typeof prevCallback === 'function' && typeof nextCallback === 'function';

    if (areFunctions) {
      var areEqual = prevCallback === nextCallback;
      var areAlike = prevCallback.toString() === nextCallback.toString();

      if (!areEqual && !areAlike) {
        // eslint-disable-next-line no-console
        console.warn('[@rushplay/forms]', 'Callbacks seem to be the same but are not comparable by reference. Consider using `React.useCallback` to avoid performance degradation');
      }
    }
  }, [nextCallback]);
}

function Provider(props) {
  if (process.env.NODE_ENV !== 'production') {
    useCallbackEqualityCheck(props.onSubmit);
  }

  var _React$useReducer = React.useReducer(reducer, getInitialState({
    schema: props.schema
  })),
      _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
      state = _React$useReducer2[0],
      dispatch = _React$useReducer2[1];

  React.useEffect(function () {
    dispatch(changeFormSchema(props.schema));
  }, [props.schema]);
  var handleFieldClear = React.useCallback(function (scope) {
    dispatch(clearField(scope));
  }, [dispatch]);
  var handleFieldsClear = React.useCallback(function (options) {
    dispatch(clearFields(options));
  }, [dispatch]);
  var handleFieldCommit = React.useCallback(function (scope) {
    dispatch(commitField(scope));
  }, [dispatch]);
  var handleFieldsCommit = React.useCallback(function (options) {
    dispatch(commitFields(options));
  }, [dispatch]);
  var handleFieldDestroy = React.useCallback(function (scope) {
    dispatch(destroyField(scope));
  }, [dispatch]);
  var handleFieldsDestroy = React.useCallback(function (options) {
    dispatch(destroyFields(options));
  }, [dispatch]);
  var handleSubmitForm = React.useCallback(function () {
    dispatch(submitForm());
    var errors = getFormErrors(state);
    props.onSubmit(errors, getFormData(state));
  }, [dispatch, props.onSubmit, state]);
  var handleSubmit = React.useCallback(function (event) {
    event.preventDefault();
    handleSubmitForm();
  }, [handleSubmitForm]);
  var context = React.useMemo(function () {
    return {
      clearField: handleFieldClear,
      clearFields: handleFieldsClear,
      commitField: handleFieldCommit,
      commitFields: handleFieldsCommit,
      destroyField: handleFieldDestroy,
      destroyFields: handleFieldsDestroy,
      dispatch: dispatch,
      name: props.name,
      state: state,
      submit: handleSubmitForm
    };
  }, [dispatch, handleFieldClear, handleSubmit, props.name, state]);
  return React.createElement(FormContext.Provider, {
    value: context
  }, React.createElement("form", {
    action: props.action,
    method: props.method,
    name: props.name,
    onSubmit: handleSubmit
  }, props.children));
}
Provider.displayName = 'FormProvider';
Provider.defaultProps = {
  onSubmit: noop
};
Provider.propTypes = {
  action: PropTypes.string,
  children: PropTypes.element,
  method: PropTypes.oneOf(['get', 'post']),
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func
};

function normalizeTransactionAmount(withdrawalTransaction, moneyBalanceCents, maxAmount) {
  return function (value) {
    if (!value) {
      return undefined;
    }

    if (withdrawalTransaction) {
      if (moneyBalanceCents) {
        var maxWithdrawValue = moneyBalanceCents / 100;

        if (value > maxWithdrawValue) {
          return parseFloat(maxWithdrawValue);
        }

        return parseFloat(value);
      }

      return parseFloat(Math.min(value, maxAmount));
    }

    return parseFloat(Math.min(value, maxAmount));
  };
}
function normalizeExpiryYear(v) {
  if (!v) {
    return v;
  }

  var value = v.replace(/\s+/g, '').replace(/\D/gi, '');
  return "20".concat(takeLast(2, value));
}
function normalizeExpiryMonth(v) {
  if (!v) {
    return v;
  }

  var value = v.replace(/\s+/g, '').replace(/\D/gi, '');
  return takeLast(2, value);
}
function normalizeCreditCardNumber(value) {
  if (!value) {
    return value;
  }

  var segment = 4;
  var v = value.replace(/\s+/g, '').replace(/\D/gi, '');
  var matches = v.match(/\d{4,16}/g);
  var match = matches && matches[0] || '';
  var parts = [];

  for (var i = 0, len = match.length; i < len; i += segment) {
    parts.push(match.substring(i, i + segment));
  }

  if (parts.length) {
    return parts.join(' ');
  }

  return v;
}
var normalizers = {
  creditCard: normalizeCreditCardNumber,
  transactionAmount: normalizeTransactionAmount,
  expiryYear: normalizeExpiryYear,
  expiryMonth: normalizeExpiryMonth
};

function parseCreditCardNumber(value) {
  if (!value) {
    return value;
  }

  return value.match(/(\d{1,4})/g).join(' ');
}
function parseExpiryYear(v) {
  if (!v) {
    return v;
  }

  var value = v.replace(/\s+/g, '').replace(/\D/gi, '');

  if (value.length >= 2) {
    return value.length === 3 ? last(value) : takeLast(2, value);
  } else {
    return value;
  }
}
function parseExpiryMonth(v) {
  if (!v) {
    return v;
  }

  var value = v.replace(/\s+/g, '').replace(/\D/gi, '');

  if (value.length >= 2) {
    return takeLast(2, value);
  } else {
    return value;
  }
}
var parsers = {
  creditCard: parseCreditCardNumber,
  expiryYear: parseExpiryYear,
  expiryMonth: parseExpiryMonth
};

export { EffectType, FieldBuilder, FieldStatus, FormContext, NodeType, Provider, uiSchema as UiSchema, changeFieldValue, changeFormSchema, clearField, clearFields, clearForm, commitField, commitFields, commitForm, createGetFieldErrorsSelector, createGetFieldSelector, createGetFieldStatusSelector, createGetFieldValueSelector, destroyField, destroyFields, getFieldValues, getFields, getFormData, getFormErrors, getInitialState, getSchema, isItemsField, isMultipleSelectField, isNumberField, isOptionField, isSingleSelectField, isTextField, normalizeCreditCardNumber, normalizeExpiryMonth, normalizeExpiryYear, normalizeTransactionAmount, normalizers, parseCreditCardNumber, parseExpiryMonth, parseExpiryYear, parsers, reducer, registerField, submitForm, useField, useFormContext, useFormData, useFormSchema, visitField };
